import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Grid,
  Typography,
  Divider,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  SelectChangeEvent,
  Snackbar,
  Alert,
} from "@mui/material";
import { DirectionsCar, DriveEta, Person } from "@mui/icons-material";
import { User } from "../passwordreqmgmt/pass_i";

interface Model {
  id: number;
  model_name: string;
  year: string;
  vehicle_photo: string;
  created_at: string;
  updated_at: string;
  status: boolean;
}

interface AddVehicleDialogProps {
  open: boolean;
  onClose: () => void;
  onVehicleAdded: () => void;
}

const AddVehicleDialog: React.FC<AddVehicleDialogProps> = ({
  open,
  onClose,
  onVehicleAdded,
}) => {
  // Form state
  const [plateNo, setPlateNo] = useState("");
  const [selectedModelId, setSelectedModelId] = useState<number>(0);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [vin, setVin] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");

  // API state
  const [users, setUsers] = useState<User[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState({
    users: false,
    models: false,
  });
  const [error, setError] = useState({
    users: null as string | null,
    models: null as string | null,
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch users and models when dialog opens
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        // Fetch users
        setLoading((prev) => ({ ...prev, users: true }));
        setError((prev) => ({ ...prev, users: null }));
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/user/all`,
          {
            headers: {
              "x-api-key": token,
            },
          }
        );

        if (!userResponse.ok) throw new Error("Failed to fetch users");
        const userData = await userResponse.json();
        setUsers(Array.isArray(userData) ? userData : []);

        // Fetch models
        setLoading((prev) => ({ ...prev, models: true }));
        setError((prev) => ({ ...prev, models: null }));
        const modelResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/vehicleModel`,
          {
            headers: {
              "x-api-key": token,
            },
          }
        );

        if (!modelResponse.ok) throw new Error("Failed to fetch models");
        const modelData = await modelResponse.json();
        setModels(Array.isArray(modelData) ? modelData : []);
      } catch (err) {
        setError((prev) => ({
          ...prev,
          // users: err.message.includes("users") ? err.message : prev.users,
          // models: err.message.includes("models") ? err.message : prev.models,
        }));
        setSnackbar({
          open: true,
          message: "Failed to load data. Please try again.",
          severity: "error",
        });
      } finally {
        setLoading((prev) => ({ ...prev, users: false, models: false }));
      }
    };

    if (open) {
      fetchData();
    } else {
      // Reset form when closing
      setPlateNo("");
      setSelectedModelId(0);
      setSelectedUserIds([]);
      setVin("");
      setUserSearch("");
      setModelSearch("");
    }
  }, [open]);

  // Filter users safely
  const filteredUsers = users.filter(
    (user) =>
      user?.name?.toLowerCase().includes(userSearch.toLowerCase()) &&
      !selectedUserIds.includes(user.id)
  );

  // Filter models safely - search by model_name
  const filteredModels = models.filter((model) =>
    model?.model_name?.toLowerCase().includes(modelSearch.toLowerCase())
  );

  const handleUserChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedUserIds(event.target.value as number[]);
  };

  const handleModelChange = (event: SelectChangeEvent<number>) => {
    setSelectedModelId(event.target.value as number);
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0 || !selectedModelId) {
      setSnackbar({
        open: true,
        message: "Please select both a model and a user",
        severity: "warning",
      });
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/vehicle/saveVehicle`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": token,
          },
          body: JSON.stringify({
            plateNo,
            vehicleModel: selectedModelId,
            vehicleOwner: selectedUserIds[0],
            vin,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to add vehicle");

      setSnackbar({
        open: true,
        message: "Vehicle added successfully!",
        severity: "success",
      });

      onVehicleAdded();
      onClose();
    } catch (error) {
      console.error("Error adding vehicle:", error);
      setSnackbar({
        open: true,
        message: "Failed to add vehicle. Please try again.",
        severity: "error",
      });
    }
  };

  // Enhanced styles
  const styles = {
    dialogPaper: {
      backgroundColor: "#1e1e1e",
      border: "2px solid #4caf50",
      borderRadius: "16px",
      width: "600px",
      maxWidth: "calc(100% - 40px)",
      maxHeight: "90vh",
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
    },
    dialogTitle: {
      color: "#4caf50",
      textAlign: "center",
      fontSize: "1.6rem",
      fontWeight: "bold",
      padding: "24px",
      borderBottom: "1px solid rgba(76, 175, 80, 0.3)",
      background: "linear-gradient(145deg, #1e1e1e 0%, #252525 100%)",
    },
    dialogContent: {
      padding: "24px",
      backgroundColor: "#1e1e1e",
      overflowY: "auto",
      "&::-webkit-scrollbar": { width: "8px" },
      "&::-webkit-scrollbar-thumb": {
        background: "#4caf50",
        borderRadius: "4px",
      },
    },
    textField: {
      "& .MuiInputBase-input": {
        color: "#ffffff",
        fontSize: "0.95rem",
        padding: "14px",
      },
      "& .MuiInputLabel-root": {
        color: "rgba(255, 255, 255, 0.7)",
        fontSize: "0.95rem",
      },
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "rgba(255, 255, 255, 0.23)",
          transition: "all 0.3s ease",
        },
        "&:hover fieldset": {
          borderColor: "#4caf50",
          boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.1)",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#4caf50",
          boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.2)",
        },
      },
    },
    chip: {
      color: "#ffffff",
      backgroundColor: "rgba(76, 175, 80, 0.2)",
      borderRadius: "8px",
      "& .MuiChip-deleteIcon": {
        color: "rgba(255, 255, 255, 0.5)",
        "&:hover": { color: "#ffffff" },
      },
    },
    dropdown: {
      backgroundColor: "#2d2d2d",
      borderRadius: "8px",
      border: "1px solid rgba(76, 175, 80, 0.5)",
      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
      "& .MuiMenu-paper": {
        backgroundColor: "#2d2d2d",
        color: "#ffffff",
      },
      "& .MuiMenuItem-root": {
        padding: "12px 16px",
        "&:hover": {
          backgroundColor: "rgba(76, 175, 80, 0.1)",
        },
        "&.Mui-selected": {
          backgroundColor: "rgba(76, 175, 80, 0.2)",
        },
      },
      "& .MuiList-root": {
        padding: "8px 0",
      },
      "& .Mui-selected": {
        backgroundColor: "rgba(76, 175, 80, 0.2)",
      },
    },
    menuItem: {
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "rgba(76, 175, 80, 0.1)",
      },
      "&.Mui-selected": {
        backgroundColor: "rgba(76, 175, 80, 0.2)",
      },
    },
    button: {
      margin: "0 12px",
      minWidth: "120px",
      padding: "10px 24px",
      fontWeight: "bold",
      borderRadius: "8px",
      textTransform: "none",
      fontSize: "0.95rem",
      transition: "all 0.3s ease",
      "&.cancel": {
        color: "#ffffff",
        border: "1px solid #f44336",
        "&:hover": {
          backgroundColor: "rgba(244, 67, 54, 0.1)",
          transform: "translateY(-2px)",
        },
      },
      "&.submit": {
        backgroundColor: "#4caf50",
        color: "#000000",
        "&:hover": {
          backgroundColor: "#3d8b40",
          transform: "translateY(-2px)",
        },
      },
    },
    sectionTitle: {
      color: "#4caf50",
      margin: "24px 0 12px 0",
      fontWeight: "bold",
      fontSize: "1.2rem",
      display: "flex",
      alignItems: "center",
      "&:before": {
        content: '""',
        display: "inline-block",
        width: "6px",
        height: "20px",
        backgroundColor: "#4caf50",
        marginRight: "12px",
        borderRadius: "3px",
      },
    },
    select: {
      "& .MuiSelect-select": {
        padding: "14px",
        minHeight: "auto !important",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.23)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4caf50",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4caf50",
        boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.2)",
      },
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: styles.dialogPaper }}
        scroll="paper"
      >
        <DialogTitle sx={styles.dialogTitle}>Add New Vehicle</DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          {/* Vehicle Model Selector Section */}
          <Box sx={{ position: "relative" }}>
            <Typography variant="h6" sx={styles.sectionTitle}>
              Vehicle Model
            </Typography>
            <Select
              fullWidth
              value={selectedModelId}
              onChange={handleModelChange}
              displayEmpty
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                      Select model...
                    </em>
                  );
                }
                const selectedModel = models.find((m) => m.id === selected);
                return selectedModel?.model_name || "Unknown model";
              }}
              sx={{ ...styles.textField, ...styles.select }}
              MenuProps={{
                PaperProps: {
                  sx: styles.dropdown,
                },
              }}
            >
              {/* Loading State */}
              {loading.models && (
                <MenuItem
                  disabled
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px",
                    color: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "#4caf50" }} />
                </MenuItem>
              )}

              {/* Error State */}
              {error.models && (
                <MenuItem
                  disabled
                  sx={{
                    color: "#f44336",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  Error: {error.models}
                </MenuItem>
              )}

              {/* No Models State */}
              {!loading.models &&
                !error.models &&
                filteredModels.length === 0 && (
                  <MenuItem disabled sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
                    {models.length === 0
                      ? "No models available"
                      : "No matching models found"}
                  </MenuItem>
                )}

              {/* Model Items */}
              {!loading.models &&
                !error.models &&
                filteredModels.map((model) => (
                  <MenuItem
                    key={model.id}
                    value={model.id}
                    sx={styles.menuItem}
                  >
                    {model.model_name} ({model.year})
                  </MenuItem>
                ))}
            </Select>
          </Box>

          <Divider
            sx={{
              my: 3,
              borderColor: "rgba(255, 255, 255, 0.12)",
              opacity: 0.5,
            }}
          />

          {/* Vehicle Details Section */}
          <Box>
            <Typography variant="h6" sx={styles.sectionTitle}>
              Vehicle Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plate Number"
                  value={plateNo}
                  onChange={(e) => setPlateNo(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                        >
                          (Plate Number)
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={styles.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="VIN"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                        >
                          (VIN)
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={styles.textField}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider
            sx={{
              my: 3,
              borderColor: "rgba(255, 255, 255, 0.12)",
              opacity: 0.5,
            }}
          />

          {/* User Assignment Section */}
          <Box sx={{ position: "relative" }}>
            <Typography variant="h6" sx={styles.sectionTitle}>
              Assign to User
            </Typography>

            <Select
              multiple
              fullWidth
              displayEmpty
              value={selectedUserIds}
              onChange={handleUserChange}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                      Select user...
                    </em>
                  );
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {users
                      .filter((user) => selected.includes(user.id))
                      .map((user) => (
                        <Chip
                          key={user.id}
                          label={user.name}
                          onDelete={() =>
                            setSelectedUserIds((prev) =>
                              prev.filter((id) => id !== user.id)
                            )
                          }
                          sx={styles.chip}
                        />
                      ))}
                  </Box>
                );
              }}
              sx={{ ...styles.textField, ...styles.select }}
              MenuProps={{
                PaperProps: {
                  sx: styles.dropdown,
                },
              }}
            >
              {/* Loading State */}
              {loading.users && (
                <MenuItem
                  disabled
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px",
                    color: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "#4caf50" }} />
                </MenuItem>
              )}

              {/* Error State */}
              {error.users && (
                <MenuItem
                  disabled
                  sx={{
                    color: "#f44336",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  Error: {error.users}
                </MenuItem>
              )}

              {/* No Users State */}
              {!loading.users && !error.users && filteredUsers.length === 0 && (
                <MenuItem disabled sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
                  {users.length === 0
                    ? "No users available"
                    : "No matching users found"}
                </MenuItem>
              )}

              {/* User Items */}
              {!loading.users &&
                !error.users &&
                filteredUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id} sx={styles.menuItem}>
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      sx={{
                        color: "#4caf50",
                        "&.Mui-checked": {
                          color: "#4caf50",
                        },
                      }}
                    />
                    <ListItemText primary={user.name} />
                  </MenuItem>
                ))}
            </Select>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid rgba(76, 175, 80, 0.3)",
            justifyContent: "center",
            background:
              "linear-gradient(145deg, rgba(30,30,30,0.9) 0%, rgba(42,42,42,0.9) 100%)",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Button
            onClick={onClose}
            sx={{ ...styles.button }}
            className="cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ ...styles.button }}
            className="submit"
            disabled={selectedUserIds.length === 0 || !selectedModelId}
          >
            Add Vehicle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor:
              snackbar.severity === "error"
                ? "#d32f2f"
                : snackbar.severity === "warning"
                ? "#ff9800"
                : "#43a047",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddVehicleDialog;
